import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { bjToast } from '@components/common';
import { useLLMWebSocket, WebSocketData } from '@hooks/useLLMData/useLLMWebSocket';


/** Constants */
const OPEN_API_WS_URL = process.env.NEXT_PUBLIC_OPEN_API_WS_URL ?? '';


/** Props */
interface UseGenerateAiDescriptionProps {
  URL?: string,
  errorToastMessage?: string,
}

/**
 * @description Custom hook to generate AI description
 * (ie: job description, professional experience, study experience, etc.).
 *
 * **Returns**
 * - `aiDescription`: Generated AI description.
 * - `setAiDescription`: Function to set AI description.
 * - `isGenerating`: Same as isLoading, but for generating AI description.
 * - `setIsGenerating`: Function to set isGenerating.
 * - `generateAiDescription`: Function to generate AI description.
 * - `stopGeneratingAiDescription`: Function to stop generating AI description.
 * - `aiGenerationDuration`: AI generation duration in milliseconds.
 *
 * @example
 * // Usage
 * const { aiDescription, isGenerating, generateAiDescription } = useGenerateAiDescription({
 *  URL: '/job-description',
 *  errorToastMessage: 'global.something.went.wrong',
 * });
 *
 * // With use effect
 * useEffect(() => {
 *  generateAiDescription({ title: 'Software tester', language: 'ro', format: 'no nested list' });
 * }, [generateAiDescription]);
 *
 * // on button click
 * <Button onClick={() => generateAiDescription({ title: 'Software tester', language: 'ro' })} />
 */
export const useLLMDescription = (props: UseGenerateAiDescriptionProps) => {
  // Destructure props
  const {
    URL,
    errorToastMessage,
  } = props;

  // Translation
  const { t } = useTranslation();

  const {
    isFullWsUrl,
    closeWSConnection,
    talkWithWS,
  } = useLLMWebSocket();

  // States
  // *************************
  const url = useRef(isFullWsUrl(URL) ? URL as string : `${OPEN_API_WS_URL}${URL}`);
  const [isGenerating, setIsGenerating] = useState<boolean | undefined>(undefined);
  const [aiDescription, setAiDescription] = useState<string | undefined>(undefined);
  const [aiGenerationDuration, setAiGenerationDuration] = useState<number>(0);


  /**
   * @description Generate AI description (ie: job description, professional experience, etc.).
   * - `postData`: Data to send to WebSocket server.
   * - `isEndCallback`: Callback function to run when the generation is finished or stopped.
   *
   * @example
   * // With use effect
   * useEffect(() => {
   *  generateAiDescription({ title: 'Software tester', language: 'ro', format: 'no nested list' });
   * }, [generateAiDescription]);
   *
   * // on button click
   * <Button onClick={() => generateAiDescription({ title: 'Software tester', language: 'ro' })} />
   */
  const generateAiDescription = useCallback((postData?: WebSocketData, isEndCallback?: () => void) => {
    const startTimeMs = Date.now();
    setIsGenerating(true);
    talkWithWS(
      url.current,
      postData,
      (data) => {
        if (data.isStart) {
          setAiDescription('');
        } else if (data.isEnd) {
          closeWSConnection();
          setIsGenerating(false);
          setAiGenerationDuration(Date.now() - startTimeMs);
          if (isEndCallback) isEndCallback();
        } else if (data.isError) {
          closeWSConnection();
          setIsGenerating(false);
          setAiGenerationDuration(Date.now() - startTimeMs);
          bjToast.error(t(errorToastMessage ?? 'global.something.went.wrong'));
        } else {
          setAiDescription(data.message);
        }
      },
    );
  }, [closeWSConnection, talkWithWS, errorToastMessage, t]);

  /**
   * @description Stop generating AI description.
   */
  const stopGeneratingAiDescription = useCallback(() => {
    closeWSConnection();
    setIsGenerating(false);
  }, [closeWSConnection]);


  // Return
  // *************************
  return {
    aiDescription,
    setAiDescription,
    isGenerating,
    setIsGenerating,
    generateAiDescription,
    stopGeneratingAiDescription,
    aiGenerationDuration,
  };
};
